import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency, cn } from '../../lib/utils';
import { usePrimaryVirtualAccount } from '../../hooks/useCrypto';
import {
  useDefiPortfolio,
  usePoolWithdraw,
  PoolPosition,
  getRiskColor,
  getRiskLabel,
  getNetworkColor,
  getProviderLogo,
} from '../../hooks/useDefi';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'ACTIVE', label: 'Ativas' },
  { value: 'WITHDRAWING', label: 'Em Resgate' },
  { value: 'WITHDRAWN', label: 'Resgatadas' },
];

const PROVIDER_OPTIONS = [
  { value: '', label: 'Todos os providers' },
  { value: 'uniswap', label: 'Uniswap' },
  { value: 'pancakeswap', label: 'PancakeSwap' },
  { value: 'jupiter', label: 'Jupiter' },
  { value: 'raydium', label: 'Raydium' },
];

export function BankingDefiPositions() {
  const navigate = useNavigate();
  const { hasCategory } = useAuthStore();
  
  // Get primary virtual account ID from banking API
  const { data: primaryAccount, isLoading: accountLoading } = usePrimaryVirtualAccount();
  const virtualAccountId = primaryAccount?.id || '';

  const [filters, setFilters] = useState<{ status?: string; provider?: string }>({});
  const [selectedPosition, setSelectedPosition] = useState<PoolPosition | null>(null);

  const { portfolio, positions, isLoading: portfolioLoading } = useDefiPortfolio(virtualAccountId);
  const withdrawMutation = usePoolWithdraw(virtualAccountId);
  
  const isLoading = accountLoading || portfolioLoading;

  // Check if crypto is enabled
  if (!hasCategory('CRYPTO')) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            DeFi não habilitado
          </h2>
          <p className="text-surface-400">
            Esta funcionalidade não está disponível para sua conta.
          </p>
        </Card>
      </div>
    );
  }
  
  // Check if user has a virtual account
  if (!accountLoading && !virtualAccountId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Conta não encontrada
          </h2>
          <p className="text-surface-400">
            Você precisa ter uma conta virtual para acessar funcionalidades DeFi.
          </p>
        </Card>
      </div>
    );
  }

  const filteredPositions = positions.filter(pos => {
    if (filters.status && pos.status !== filters.status) return false;
    if (filters.provider && pos.pool.provider !== filters.provider) return false;
    return true;
  });

  const handleWithdraw = async () => {
    if (!selectedPosition) return;
    
    try {
      await withdrawMutation.mutateAsync({
        positionId: selectedPosition.id,
      });
      setSelectedPosition(null);
    } catch (error) {
      console.error('Error withdrawing:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/banking/defi/pools')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Layers className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Minhas Posições</h1>
            <p className="text-surface-400">Gerencie seus investimentos DeFi</p>
          </div>
        </div>
      </div>

      {/* Portfolio Summary */}
      <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-surface-400 text-sm">Total Investido</p>
            <p className="text-2xl font-bold text-surface-100">
              {formatCurrency(portfolio.totalDepositedBrl / 100)}
            </p>
          </div>
          <div>
            <p className="text-surface-400 text-sm">Valor Atual</p>
            <p className="text-2xl font-bold text-surface-100">
              {formatCurrency(portfolio.currentValueBrl / 100)}
            </p>
          </div>
          <div>
            <p className="text-surface-400 text-sm">Rendimentos</p>
            <p className={cn(
              'text-2xl font-bold',
              portfolio.totalEarnings >= 0 ? 'text-green-400' : 'text-red-400'
            )}>
              {portfolio.totalEarnings >= 0 ? '+' : ''}{formatCurrency(portfolio.totalEarnings / 100)}
            </p>
          </div>
          <div>
            <p className="text-surface-400 text-sm">Retorno</p>
            <p className={cn(
              'text-2xl font-bold',
              portfolio.totalEarningsPercentage >= 0 ? 'text-green-400' : 'text-red-400'
            )}>
              {portfolio.totalEarningsPercentage >= 0 ? '+' : ''}{portfolio.totalEarningsPercentage.toFixed(2)}%
            </p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex gap-4">
        <Select
          options={STATUS_OPTIONS}
          value={filters.status || ''}
          onChange={(e) => setFilters(f => ({ ...f, status: e.target.value || undefined }))}
        />
        <Select
          options={PROVIDER_OPTIONS}
          value={filters.provider || ''}
          onChange={(e) => setFilters(f => ({ ...f, provider: e.target.value || undefined }))}
        />
      </div>

      {/* Positions List */}
      {filteredPositions.length === 0 ? (
        <Card className="text-center py-12">
          <Layers className="w-12 h-12 mx-auto mb-4 text-surface-500" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">
            Nenhuma posição encontrada
          </h3>
          <p className="text-surface-400 mb-4">
            Você ainda não tem investimentos em pools DeFi
          </p>
          <Button onClick={() => navigate('/banking/defi/pools')}>
            Explorar Pools
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPositions.map((position) => (
            <PositionCard
              key={position.id}
              position={position}
              onWithdraw={() => setSelectedPosition(position)}
            />
          ))}
        </div>
      )}

      {/* Withdraw Modal */}
      <Modal
        isOpen={!!selectedPosition}
        onClose={() => setSelectedPosition(null)}
        title="Resgatar Posição"
      >
        {selectedPosition && (
          <div className="space-y-6">
            {/* Position Info */}
            <Card className="bg-surface-800/50">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getNetworkColor(selectedPosition.pool.network)} flex items-center justify-center text-2xl`}>
                  {getProviderLogo(selectedPosition.pool.provider)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-surface-100">{selectedPosition.pool.name}</h3>
                  <p className="text-surface-400 text-sm">{selectedPosition.pool.provider} • {selectedPosition.pool.network}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-surface-400 text-sm">Valor Depositado</p>
                  <p className="text-xl font-bold text-surface-100">
                    {formatCurrency(selectedPosition.depositedAmountBrl / 100)}
                  </p>
                </div>
                <div>
                  <p className="text-surface-400 text-sm">Valor Atual</p>
                  <p className="text-xl font-bold text-green-400">
                    {formatCurrency(selectedPosition.currentValueBrl / 100)}
                  </p>
                </div>
              </div>
            </Card>

            {/* Earnings Summary */}
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <p className="text-surface-400 text-sm">Rendimento Total</p>
              <p className="text-xl font-bold text-green-400">
                +{formatCurrency(selectedPosition.earnings / 100)} ({selectedPosition.earningsPercentage.toFixed(2)}%)
              </p>
            </div>

            {/* Warning */}
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-400 font-medium">Atenção</p>
                  <p className="text-surface-400 text-sm">
                    O resgate pode levar alguns minutos para ser processado. O valor será creditado em sua conta virtual.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setSelectedPosition(null)}>
                Cancelar
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={withdrawMutation.isPending}
              >
                {withdrawMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Resgatando...
                  </>
                ) : (
                  'Confirmar Resgate'
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// PositionCard Component
interface PositionCardProps {
  position: PoolPosition;
  onWithdraw: () => void;
}

function PositionCard({ position, onWithdraw }: PositionCardProps) {
  const isPositive = position.earnings >= 0;
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-500/20 text-green-400">Ativa</Badge>;
      case 'WITHDRAWING':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Em Resgate</Badge>;
      case 'WITHDRAWN':
        return <Badge className="bg-surface-500/20 text-surface-400">Resgatada</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className="hover:border-primary-500/50 transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Pool Info */}
        <div className="flex items-center gap-3 flex-1">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getNetworkColor(position.pool.network)} flex items-center justify-center text-2xl`}>
            {getProviderLogo(position.pool.provider)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-surface-100">{position.pool.name}</h3>
              {getStatusBadge(position.status)}
            </div>
            <p className="text-surface-400 text-sm capitalize">
              {position.pool.provider} • {position.pool.network}
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="grid grid-cols-3 gap-6 text-center md:text-right">
          <div>
            <p className="text-surface-500 text-xs mb-1">Depositado</p>
            <p className="text-lg font-semibold text-surface-100">
              {formatCurrency(position.depositedAmountBrl / 100)}
            </p>
          </div>
          <div>
            <p className="text-surface-500 text-xs mb-1">Valor Atual</p>
            <p className="text-lg font-semibold text-surface-100">
              {formatCurrency(position.currentValueBrl / 100)}
            </p>
          </div>
          <div>
            <p className="text-surface-500 text-xs mb-1">Rendimento</p>
            <div className="flex items-center justify-center md:justify-end gap-1">
              {isPositive ? (
                <TrendingUp className="w-4 h-4 text-green-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
              <span className={cn(
                'text-lg font-semibold',
                isPositive ? 'text-green-400' : 'text-red-400'
              )}>
                {isPositive ? '+' : ''}{position.earningsPercentage.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {position.txHash && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`https://etherscan.io/tx/${position.txHash}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          {position.status === 'ACTIVE' && (
            <Button onClick={onWithdraw}>
              Resgatar
            </Button>
          )}
          {position.status === 'WITHDRAWING' && (
            <Button disabled>
              <Clock className="w-4 h-4 mr-2" />
              Processando
            </Button>
          )}
        </div>
      </div>

      {/* Deposit Date */}
      <div className="mt-4 pt-4 border-t border-surface-800 flex items-center gap-2 text-surface-500 text-sm">
        <Clock className="w-4 h-4" />
        <span>Depositado em {new Date(position.depositedAt).toLocaleDateString('pt-BR')}</span>
        <Badge className={getRiskColor(position.pool.riskLevel)}>
          {getRiskLabel(position.pool.riskLevel)}
        </Badge>
      </div>
    </Card>
  );
}
